import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { BookOpen, Calendar, FileText, Plus, CloudRain, Sun, Leaf, LayoutGrid, List, CheckSquare, Square, Download, Trash2, CheckCircle2, RotateCcw, AlertTriangle } from 'lucide-react';

export default function ApprenticeJournal() {
    const { user: authUser, fetchWithAuth } = useContext(AuthContext);
    const { addToast } = useToast();
    const [weeks, setWeeks] = useState([]);
    const [trashWeeks, setTrashWeeks] = useState([]);
    const [currentTab, setCurrentTab] = useState('active'); // 'active' | 'trash'
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('journalViewMode') || 'grid');
    const [selectedIds, setSelectedIds] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, id: null });
    const navigate = useNavigate();

    useEffect(() => {
        localStorage.setItem('journalViewMode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        if (currentTab === 'active') {
            fetchWeeks();
        } else {
            fetchTrash();
        }
    }, [currentTab]);

    const fetchWeeks = async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth('/api/apprentice/weeks/');
            if (response.ok) {
                const data = await response.json();
                setWeeks(data);
            }
        } catch (error) {
            console.error('Error fetching weeks:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTrash = async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth('/api/apprentice/weeks/trash/');
            if (response.ok) {
                const data = await response.json();
                setTrashWeeks(data);
            }
        } catch (error) {
            console.error('Error fetching trash:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = (yearWeeks) => {
        const yearIds = yearWeeks.map(w => w.id);
        const allSelected = yearIds.every(id => selectedIds.includes(id));

        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !yearIds.includes(id)));
        } else {
            setSelectedIds(prev => [...new Set([...prev, ...yearIds])]);
        }
    };

    const handleDeleteWeek = (e, id) => {
        if (e && e.stopPropagation) e.stopPropagation();
        
        setConfirmModal({
            show: true,
            title: 'Woche löschen',
            message: 'Möchtest du diese Woche wirklich in den Papierkorb verschieben? (CUSTOM MODAL VER 1)',
            id: id,
            onConfirm: async (idToDelete) => {
                try {
                    const response = await fetchWithAuth(`/api/apprentice/weeks/${idToDelete}/`, {
                        method: 'DELETE'
                    });
                    if (response.ok) {
                        setWeeks(prev => {
                            const filtered = prev.filter(w => w.id !== idToDelete);
                            return filtered;
                        });
                        setSelectedIds(prev => prev.filter(i => i !== idToDelete));
                        addToast('Woche in den Papierkorb verschoben', 'success');
                    } else {
                        addToast(`Fehler beim Verschieben (${response.status})`, 'error');
                    }
                } catch (error) {
                    addToast('Kritischer Fehler beim Löschen', 'error');
                }
            }
        });
    };

    const handleRestoreWeek = async (e, id) => {
        e.stopPropagation();
        try {
            const response = await fetchWithAuth(`/api/apprentice/weeks/${id}/restore/`, {
                method: 'POST'
            });
            if (response.ok) {
                setTrashWeeks(prev => prev.filter(w => w.id !== id));
                // Reload weeks from server to be sure or just optimistic update if possible
                fetchWeeks(); 
                addToast('Woche wiederhergestellt', 'success');
            } else {
                addToast('Fehler beim Wiederherstellen', 'error');
            }
        } catch (error) {
            console.error('Error restoring week:', error);
            addToast('Fehler beim Wiederherstellen', 'error');
        }
    };

    const handleForceDeleteWeek = (e, id) => {
        if (e && e.stopPropagation) e.stopPropagation();
        setConfirmModal({
            show: true,
            title: 'Endgültig löschen',
            message: 'Möchtest du diese Woche wirklich UNWIDERRUFLICH löschen? Dies kann nicht rückgängig gemacht werden. (CUSTOM MODAL VER 1)',
            id: id,
            onConfirm: async (idToDelete) => {
                try {
                    const response = await fetchWithAuth(`/api/apprentice/weeks/${idToDelete}/force_delete/`, {
                        method: 'DELETE'
                    });
        
                    if (response.ok) {
                        setTrashWeeks(prev => prev.filter(w => w.id !== idToDelete));
                        addToast('Woche endgültig gelöscht', 'success');
                    } else {
                        addToast('Fehler beim endgültigen Löschen', 'error');
                    }
                } catch (error) {
                    console.error('Error force deleting week:', error);
                    addToast('Kritischer Fehler beim Löschen', 'error');
                }
            }
        });
    };

    const handleBatchDownload = async () => {
        if (selectedIds.length === 0) return;
        setIsDownloading(true);
        try {
            const response = await fetchWithAuth('/api/apprentice/weeks/batch_download_zip/', {
                method: 'POST',
                body: JSON.stringify({ ids: selectedIds })
            });

            if (response.ok) {
                const arrayBuffer = await response.arrayBuffer();
                const blob = new Blob([arrayBuffer], { type: response.headers.get('content-type') || 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Ausbildungsnachweise_Batch_${new Date().toISOString().split('T')[0]}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                addToast('Fehler beim Herunterladen der ZIP-Datei.', 'error');
            }
        } catch (error) {
            console.error('Error downloading batch ZIP:', error);
            addToast('Fehler beim Herunterladen der ZIP-Datei.', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleBatchPdwExport = async () => {
        if (selectedIds.length === 0) return;
        setIsDownloading(true);
        try {
            const response = await fetchWithAuth('/api/apprentice/pflanze-der-woche/batch_export_from_weeks/', {
                method: 'POST',
                body: JSON.stringify({ week_ids: selectedIds })
            });

            if (response.ok) {
                const arrayBuffer = await response.arrayBuffer();
                const blob = new Blob([arrayBuffer], { type: 'application/zip' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Pflanzen_der_Woche_Batch_${new Date().toISOString().split('T')[0]}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                addToast('Pflanzen der Woche erfolgreich exportiert.', 'success');
            } else {
                const errorData = await response.json().catch(() => ({}));
                addToast(errorData.error || 'Fehler beim Export der Pflanzen der Woche.', 'error');
            }
        } catch (error) {
            console.error('Error exporting batch PDWs:', error);
            addToast('Fehler beim Export der Pflanzen der Woche.', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    // Group weeks by year
    const activeDataToGroup = currentTab === 'active' ? weeks : trashWeeks;
    const groupedWeeks = activeDataToGroup.reduce((acc, week) => {
        const year = week.year;
        if (!acc[year]) acc[year] = [];
        acc[year].push(week);
        return acc;
    }, {});

    // Sort years descending
    const sortedYears = Object.keys(groupedWeeks).sort((a, b) => b - a);

    const WeekCard = ({ week, isSelected, onSelect, onDelete, onRestore, onForceDelete, isTrash }) => {
        return (
            <div
                className={`group relative overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-xl border-2 ${
                    isSelected 
                    ? 'border-plantlix-500 ring-4 ring-plantlix-500/10' 
                    : 'border-gray-100 dark:border-gray-800'
                }`}
            >
                {/* Visual Accent */}
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${week.is_approved ? 'from-plantlix-500 to-plantlix-400' : 'from-blue-400 to-blue-500'}`} />

                {/* Selection Overlay */}
                <div
                    onClick={(e) => { e.stopPropagation(); onSelect(e); }}
                    className={`absolute top-4 left-4 z-10 p-1.5 rounded-lg transition-all cursor-pointer ${
                        isSelected 
                        ? 'bg-plantlix-500 text-white' 
                        : 'bg-white/90 dark:bg-black/40 text-gray-400 hover:text-plantlix-500 opacity-0 group-hover:opacity-100'
                    }`}
                >
                    {isSelected ? <CheckCircle2 className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                </div>

                {/* Trash Actions */}
                {!isTrash ? (
                    <button
                        onClick={(e) => onDelete(e)}
                        className="absolute top-4 right-4 z-10 p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                ) : (
                    <div className="absolute top-4 right-4 z-10 flex gap-2">
                        <button
                            onClick={(e) => onRestore(e)}
                            className="p-2 text-plantlix-500 bg-plantlix-50 dark:bg-plantlix-900/20 rounded-xl"
                        >
                            <RotateCcw className="w-5 h-5" />
                        </button>
                        <button
                            onClick={(e) => onForceDelete(e)}
                            className="p-2 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
                )}

                <div 
                    onClick={() => currentTab === 'active' ? navigate(`/apprentice/week/${week.id}`) : null}
                    className="cursor-pointer"
                >
                    <div className="flex justify-between items-start mb-6 pt-4">
                        <div>
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white leading-tight">
                                KW {week.week_number}
                                <span className="text-gray-300 dark:text-gray-700 mx-2">/</span>
                                <span className="text-gray-400 dark:text-gray-500">{week.year}</span>
                            </h3>
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mt-1">
                                {new Date(week.start_date).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit'})} - {new Date(week.end_date).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'})}
                            </p>
                        </div>
                        {week.is_approved && (
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-500 rounded-full text-[10px] font-black uppercase tracking-tighter border border-plantlix-100 dark:border-plantlix-800">
                                <CheckCircle2 className="w-3 h-3" />
                                Freigegeben
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between items-end mb-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Fortschritt</span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">{week.completion_percentage}%</span>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 p-0.5 border border-gray-50 dark:border-gray-700">
                                <div
                                    className={`h-full rounded-full transition-all duration-1000 ${
                                        week.completion_percentage === 100 
                                        ? 'bg-gradient-to-r from-plantlix-500 to-plantlix-400' 
                                        : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                                    }`}
                                    style={{ width: `${week.completion_percentage}%` }}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-50 dark:border-gray-800">
                            <div className="flex flex-col">
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Dokumentiert</span>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <FileText className="w-3.5 h-3.5 text-blue-500" />
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{week.report_count}/7</span>
                                </div>
                            </div>
                            {week.temp_max && (
                                <div className="flex flex-col">
                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Duchschn. Wetter</span>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <Sun className="w-3.5 h-3.5 text-orange-400" />
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{week.temp_max}°C</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const WeekListRow = ({ week, isSelected, onSelect, onDelete, onRestore, onForceDelete, isTrash }) => {
        return (
            <div
                onClick={() => !isTrash ? navigate(`/apprentice/week/${week.id}`) : null}
                className={`flex items-center p-4 bg-white dark:bg-[#1a1a1a] border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer group ${isSelected ? 'bg-plantlix-50/50 dark:bg-plantlix-900/10' : ''}`}
            >
                <div
                    onClick={(e) => onSelect(e)}
                    className="mr-4 p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                    {isSelected ? (
                        <CheckSquare className="w-5 h-5 text-plantlix-500" />
                    ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                    )}
                </div>

                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <div>
                        <span className="font-bold text-gray-900 dark:text-white">KW {week.week_number}/{week.year}</span>
                        <p className="text-xs text-gray-500 font-mono">
                            {new Date(week.start_date).toLocaleDateString('de-DE')} - {new Date(week.end_date).toLocaleDateString('de-DE')}
                        </p>
                    </div>

                    <div className="hidden md:block">
                        <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 max-w-[100px]">
                                <div
                                    className="bg-plantlix-500 h-1.5 rounded-full"
                                    style={{ width: `${week.completion_percentage}%` }}
                                />
                            </div>
                            <span className="text-xs font-medium text-gray-500">{week.completion_percentage}%</span>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-3">
                        <div className="flex items-center gap-1 text-gray-400 text-xs">
                            <Sun className="w-3.5 h-3.5" />
                            <span>{week.temp_max}°C</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400 text-xs">
                            <CloudRain className="w-3.5 h-3.5" />
                            <span>{week.temp_min}°C</span>
                        </div>
                    </div>

                    <div className="flex justify-between md:justify-end items-center gap-4">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <FileText className="w-3.5 h-3.5" />
                            <span>{week.report_count}/7</span>
                        </div>
                        {week.is_approved ? (
                            <CheckCircle2 className="w-5 h-5 text-plantlix-500" />
                        ) : (
                            <div className="w-5 h-5" />
                        )}
                        
                        {!isTrash ? (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(e); }}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="In den Papierkorb verschieben"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onRestore(e); }}
                                    className="p-1.5 text-plantlix-500 hover:text-plantlix-600 bg-plantlix-50 dark:bg-plantlix-900/20 hover:bg-plantlix-100 dark:hover:bg-plantlix-900/40 rounded-lg transition-colors"
                                    title="Wiederherstellen"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); onForceDelete(e); }}
                                    className="p-1.5 text-red-500 hover:text-red-700 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                                    title="Endgültig löschen"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="animate-in slide-in-from-left duration-700">
                        <div className="flex items-center gap-4 mb-3">
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl shadow-premium border border-gray-100 dark:border-gray-800">
                                <BookOpen className="w-8 h-8 text-plantlix-500 dark:text-plantlix-400" />
                            </div>
                            <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">
                                Journal
                            </h1>
                        </div>
                        <p className="text-lg text-gray-500 dark:text-gray-400 font-medium">
                            Dein digitales Berichtsheft <span className="text-plantlix-500 opacity-50 px-1">•</span> <span className="italic">Plantlix Pro</span>
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-800 rounded-2xl p-1.5 shadow-sm">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-plantlix-500 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <LayoutGrid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-xl transition-all ${viewMode === 'list' ? 'bg-plantlix-500 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>

                        {selectedIds.length > 0 && (
                            <div className="flex items-center gap-2 animate-in zoom-in duration-300">
                                <button
                                    onClick={handleBatchDownload}
                                    disabled={isDownloading}
                                    className="flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-black text-white rounded-2xl font-bold text-sm hover:scale-105 transition-all shadow-xl disabled:opacity-50"
                                >
                                    <Download className={`w-4 h-4 ${isDownloading ? 'animate-bounce' : ''}`} />
                                    <span className="hidden sm:inline">Export ZIP</span>
                                    <span className="sm:hidden">{selectedIds.length} ZIP</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-gradient-to-r from-plantlix-500/10 to-plantlix-500/10 dark:from-plantlix-900/20 dark:to-plantlix-700/20 rounded-lg p-6 mb-8 border border-plantlix-500/20">
                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                Neue Woche starten
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Erstelle einen neuen Wochenbericht mit automatischen Wetterdaten
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg px-3 py-1.5 shadow-sm">
                                <span className="text-xs font-bold text-gray-500 uppercase">Jahr:</span>
                                <select
                                    id="yearSelector"
                                    className="text-sm bg-transparent border-none focus:ring-0 text-gray-700 dark:text-gray-300 font-medium cursor-pointer"
                                    defaultValue={new Date().getFullYear()}
                                >
                                    {[2024, 2025, 2026, 2027].map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg px-3 py-1.5 shadow-sm">
                                <span className="text-xs font-bold text-gray-500 uppercase">KW:</span>
                                <select
                                    id="weekSelector"
                                    className="text-sm bg-transparent border-none focus:ring-0 text-gray-700 dark:text-gray-300 font-medium cursor-pointer"
                                    defaultValue={(() => {
                                        const now = new Date();
                                        const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
                                        const dayNum = d.getUTCDay() || 7;
                                        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
                                        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
                                        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
                                    })()}
                                >
                                    {Array.from({ length: 53 }, (_, i) => i + 1).map(w => (
                                        <option key={w} value={w}>{w}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={() => {
                                    const year = document.getElementById('yearSelector').value;
                                    const week = document.getElementById('weekSelector').value;
                                    navigate(`/apprentice/week/new?year=${year}&week=${week}`);
                                }}
                                className="flex items-center gap-2 px-6 py-3 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Neue Woche</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Pflanze der Woche Teaser */}
                <div className="bg-gradient-to-r from-plantlix-500/10 to-plantlix-500/10 dark:from-plantlix-900/20 dark:to-plantlix-700/20 rounded-lg p-6 mb-8 border border-plantlix-500/20">
                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <Leaf className="w-5 h-5 text-plantlix-600 dark:text-plantlix-400" />
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Pflanze der Woche
                                </h2>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Erstelle und lade dir die "Pflanze der Woche" PDFs passend zu deinem Ausbildungsnachweis herunter.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/apprentice/pflanze-der-woche')}
                            className="flex items-center gap-2 px-6 py-3 bg-plantlix-600 hover:bg-plantlix-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                        >
                            <span>Zu den Einträgen</span>
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex space-x-1 mb-8 bg-gray-100/50 dark:bg-gray-800/50 p-1 rounded-xl">
                    <button
                        onClick={() => setCurrentTab('active')}
                        className={`flex-1 flex items-center justify-center py-2.5 px-4 rounded-lg font-semibold transition-all ${
                            currentTab === 'active' 
                            ? 'bg-white dark:bg-gray-700 text-plantlix-500 dark:text-plantlix-400 shadow-sm ring-1 ring-gray-200 dark:ring-gray-600' 
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                        Aktive Berichte
                    </button>
                    <button
                        onClick={() => setCurrentTab('trash')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-semibold transition-all ${
                            currentTab === 'trash' 
                            ? 'bg-white dark:bg-gray-700 text-red-500 dark:text-red-400 shadow-sm ring-1 ring-gray-200 dark:ring-gray-600' 
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                        <Trash2 className="w-4 h-4" />
                        Papierkorb
                    </button>
                </div>

                {/* Info Box if Trash is Empty/Full */}
                {currentTab === 'trash' && trashWeeks.length > 0 && (
                    <div className="flex justify-between items-center mb-6 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 p-4 rounded-lg border border-red-200 dark:border-red-800">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                            <p className="text-sm font-medium">Berichte im Papierkorb werden (noch) nicht automatisch gelöscht. Du kannst sie wiederherstellen oder dauerhaft vernichten.</p>
                        </div>
                    </div>
                )}

                {/* Weeks List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-plantlix-500"></div>
                        <p className="mt-4 text-gray-600 dark:text-gray-400">{currentTab === 'active' ? 'Lade Wochenberichte...' : 'Lade den Papierkorb...'}</p>
                    </div>
                ) : activeDataToGroup.length === 0 ? (
                    <div className="text-center py-12 bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700">
                        {currentTab === 'active' ? (
                            <>
                                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    Noch keine Wochenberichte
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Starte jetzt mit deinem ersten digitalen Ausbildungsnachweis
                                </p>
                                <button
                                    onClick={() => navigate('/apprentice/week/new')}
                                    className="px-6 py-3 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg font-semibold transition-all"
                                >
                                    Erste Woche erstellen
                                </button>
                            </>
                        ) : (
                            <>
                                <Trash2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    Papierkorb ist leer
                                </h3>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="space-y-12">
                        {sortedYears.map(year => (
                            <div key={year}>
                                <div className="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-gray-700 pb-2">
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        Jahr {year}
                                    </h2>
                                    {viewMode === 'list' && (
                                        <button
                                            onClick={() => toggleSelectAll(groupedWeeks[year])}
                                            className="text-xs font-bold text-plantlix-500 hover:text-plantlix-600 dark:text-plantlix-400 px-3 py-1 rounded-md hover:bg-plantlix-500/10 transition-all flex items-center gap-2"
                                        >
                                            <Square className="w-4 h-4" />
                                            {groupedWeeks[year].every(w => selectedIds.includes(w.id)) ? 'Alle abwählen' : 'Alle auswählen'}
                                        </button>
                                    )}
                                </div>

                                {viewMode === 'grid' ? (
                                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                                {groupedWeeks[year].map(week => (
                                            <WeekCard 
                                                key={week.id} 
                                                week={week} 
                                                isSelected={selectedIds.includes(week.id)}
                                                onSelect={(e) => {
                                                    e.stopPropagation();
                                                    toggleSelect(week.id);
                                                }}
                                                onDelete={(e) => handleDeleteWeek(e, week.id)}
                                                onRestore={(e) => handleRestoreWeek(e, week.id)}
                                                onForceDelete={(e) => handleForceDeleteWeek(e, week.id)}
                                                isTrash={currentTab === 'trash'}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                                        {groupedWeeks[year].map(week => (
                                            <WeekListRow 
                                                key={week.id} 
                                                week={week} 
                                                isSelected={selectedIds.includes(week.id)}
                                                onSelect={(e) => {
                                                    e.stopPropagation();
                                                    toggleSelect(week.id);
                                                }}
                                                onDelete={(e) => handleDeleteWeek(e, week.id)}
                                                onRestore={(e) => handleRestoreWeek(e, week.id)}
                                                onForceDelete={(e) => handleForceDeleteWeek(e, week.id)}
                                                isTrash={currentTab === 'trash'}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {/* Custom Confirmation Modal */}
                {confirmModal.show && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200 dark:border-gray-700 animate-in zoom-in-95 duration-200">
                            <div className="p-6">
                                <div className="flex items-center gap-3 mb-4 text-red-500">
                                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-full">
                                        <AlertTriangle className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-xl font-bold dark:text-white">{confirmModal.title}</h3>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 mb-6 font-medium">
                                    {confirmModal.message}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                                        className="px-5 py-2.5 rounded-xl font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        onClick={() => {
                                            confirmModal.onConfirm(confirmModal.id);
                                            setConfirmModal({ ...confirmModal, show: false });
                                        }}
                                        className="px-5 py-2.5 rounded-xl font-semibold bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 transition-all"
                                    >
                                        Löschen bestätigen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
